// src/pages/artist/[id].tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { API_BASE_URL } from '@/config';
import ArtistPageBySlug from './[slug]';

const ArtistIdRedirectPage = () => {
  const { artistIdentifier } = useParams<{ artistIdentifier: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if the identifier is a UUID (contains hyphens and is 36 characters)
  const isUUID = (identifier: string) => {
    const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return uuidPattern.test(identifier);
  };

  useEffect(() => {
    if (!artistIdentifier) {
      setError('No artist identifier provided');
      setIsRedirecting(false);
      return;
    }

    console.log(`Artist page loaded with identifier: ${artistIdentifier}`);

    const isMassivePrefixedId = artistIdentifier.startsWith('massive_');

    // If it's a UUID (normal), or a MassiveMusic prefixed id, fetch the artist data and redirect to slug.
    if (isUUID(artistIdentifier) || isMassivePrefixedId) {
      const fetchAndRedirect = async () => {
        try {
          console.log(`Fetching artist data for UUID: ${artistIdentifier}`);
          const res = await fetch(`${API_BASE_URL}/public/artist/${artistIdentifier}`);
          
          if (!res.ok) {
            console.error(`Artist not found for ID: ${artistIdentifier}, status: ${res.status}`);
            throw new Error('Artist not found');
          }

          const artist = await res.json();
          console.log(`Artist data retrieved:`, artist);
          
          // Check if artist has a slug and redirect
          if (artist?.slug) {
            console.log(`Redirecting to slug: ${artist.slug}`);
            // Preserve hash if present
            const targetPath = `/artist/${artist.slug}${location.hash}`;
            navigate(targetPath, { replace: true });
          } else {
            // If artist exists but has no slug, manually create a slug from the name
            const tempSlug = artist.name
              ? artist.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
              : artistIdentifier;
              
            console.log(`No slug found, using generated slug: ${tempSlug}`);
            // Preserve hash if present
            const targetPath = `/artist/${tempSlug}${location.hash}`;
            navigate(targetPath, { replace: true });
          }
        } catch (err) {
          console.error('Redirect failed:', err);
          setError(err instanceof Error ? err.message : 'Failed to load artist');
          setIsRedirecting(false);
        }
      };

      fetchAndRedirect();
    } else {
      // If it's already a slug (not a UUID), render the artist page directly
      console.log(`Identifier is a slug: ${artistIdentifier}`);
      setIsRedirecting(false);
    }
  }, [artistIdentifier, navigate, location.hash]);

  // If it's a slug, render the artist page component directly
  if (!isRedirecting && !error) {
    return <ArtistPageBySlug />;
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Artist ID: {artistIdentifier}
        </Typography>
      </Box>
    );
  }

  if (isRedirecting) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading artist page...
        </Typography>
      </Box>
    );
  }

  return null;
};

export default ArtistIdRedirectPage;

